import { autoinject, computedFrom, PLATFORM } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { BatchProcessingCentreApi } from '~/api/batch-processing-centre-api';
import {
	BaseDialog,
	BaseDialogModel,
	BaseDialogModelDefaults,
} from '~/components/dialog/base-dialog';
import { Notification } from '~/components/notification/notification';
import { BatchProcessFieldName } from '~/models/entities/batch-process';
import { FormModel } from '~/models/form-model';
import { FetchUtils } from '~/utils/fetch-utils';

export interface ProcessBatchDialogModel extends BaseDialogModel {
	formModel?: FormModel;
}

const ProcessBatchDialogModelDefaults: ProcessBatchDialogModel = {
	...BaseDialogModelDefaults,
	cancelButtonText: 'clickable-elements.abort',
	dialogSize: BaseDialog.DialogSize.EXTRA_SMALL,
	okButtonText: 'clickable-elements.proceed',
};

@autoinject
export class ProcessBatchDialog extends BaseDialog<ProcessBatchDialogModel> {
	private isSaving: boolean = false;

	constructor(protected readonly i18n?: I18N, ...rest) {
		super(...rest);
		PLATFORM.moduleName('./process-batch-dialog');
	}

	public activate(model: ProcessBatchDialogModel) {
		return super.activate(model, ProcessBatchDialogModelDefaults);
	}

	@computedFrom('isSaving')
	protected get canSave(): boolean {
		return !this.isSaving;
	}

	/**
	 * 'Process Batch' action
	 */
	private async processBatch() {
		if (!this.canSave) {
			return;
		}

		if (!this.model.formModel) {
			throw new Error(
				'Process Batch Dialog: Unable to get the formModel',
			);
		}

		const batchIdentityFieldModel =
			this.model.formModel.fields[BatchProcessFieldName.BSi_Identity];
		if (!batchIdentityFieldModel || !batchIdentityFieldModel.value) {
			throw new Error(
				"Process Batch Dialog: Unable to get 'BSi_Identity' value",
			);
		}

		this.isSaving = true;
		BatchProcessingCentreApi.processBatch(batchIdentityFieldModel.value)
			.then(() => {
				this.controller.ok();
			})
			.catch((error) => {
				let message = FetchUtils.normalizeError(error);
				message = message.replace('Error ', '');
				Notification.info(
					this.i18n.tr(
						'batch-processing-centre:unable-to-process-this-batch',
						{ message },
					),
				);
				this.controller.cancel();
			})
			.finally(() => {
				this.isSaving = false;
			});
	}
}
